import { registerSW } from "virtual:pwa-register";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Link, RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "./index.css";

import { ToastContainer } from "./components/ToastContainer";
import { bootstrapAuth, useAuthStore } from "./lib/auth";
import { routeTree } from "./routeTree.gen";
import { initializeTheme } from "./lib/theme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultNotFoundComponent: NotFoundPage,
  context: { queryClient, auth: useAuthStore.getState() },
});

/** App-wide 404. Replaces TanStack Router's bare "Not Found" text with a
 *  styled card and a way back into the app. */
function NotFoundPage() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-6xl font-bold tracking-tight opacity-15">404</p>
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">Page not found</h1>
        <p className="text-sm opacity-60">
          The page you&rsquo;re looking for doesn&rsquo;t exist or may have moved.
        </p>
      </div>
      <Link
        to="/home"
        className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
      >
        Go to home
      </Link>
    </div>
  );
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

initializeTheme();

registerSW({
  onNeedRefresh() {
    window.location.reload();
  },
  onOfflineReady() {},
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root element not found");

// Resolve the user's auth state before first render so route `beforeLoad`
// guards can make a synchronous redirect decision instead of flashing
// protected content for one frame.
bootstrapAuth().finally(() => {
  // Push the latest auth snapshot into the router context, then sync it on
  // every store change so guards always see the current user.
  router.update({ context: { queryClient, auth: useAuthStore.getState() } });
  useAuthStore.subscribe((state) => {
    router.update({ context: { queryClient, auth: state } });
  });

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ToastContainer />
      </QueryClientProvider>
    </StrictMode>,
  );
});
